import { Turbo } from "@hotwired/turbo-rails"

// Share one short-lived request between row previews and full-page links. A
// hover must settle before starting, and speculative requests are rate-limited.
const INTENT_DELAY_MS = 150
const MIN_INTERVAL_MS = 1000
const MAX_AGE_MS = 3000
const MAX_FRESH_ATTEMPTS = 2
// The server allows each address a fixed number of requests per window, and a
// real click needs its share. One tab skimming a list must not spend it all.
const MAX_STARTS_PER_MINUTE = 10
const BUDGET_WINDOW_MS = 60000
const THROTTLED_PAUSE_MS = 60000

// The drawer controller reconnects on every Turbo visit, but the tab and its
// allowance do not, so the budget lives here rather than on an instance.
class StartBudget {
  constructor() {
    this.starts = []
    this.lastStartedAt = -Infinity
    this.pausedUntil = -Infinity
  }

  allows(now) {
    while (this.starts.length && now - this.starts[0] >= BUDGET_WINDOW_MS) this.starts.shift()
    return now >= this.pausedUntil && this.starts.length < MAX_STARTS_PER_MINUTE
  }

  spacing(now) {
    return Math.max(0, this.lastStartedAt + MIN_INTERVAL_MS - now)
  }

  record(now) {
    this.starts.push(now)
    this.lastStartedAt = now
  }

  pause(now) {
    this.pausedUntil = now + THROTTLED_PAUSE_MS
  }
}

const budget = new StartBudget()

export default class ResourcePreviewPrefetch {
  constructor(frame) {
    this.frame = frame
    this.active = new Set()
    this.onSchedule = event => this.schedule(event.detail?.url)
    this.onCancel = event => this.cancel(event.detail?.url)
    this.onInvalidate = () => this.invalidate()
    this.onBeforeCache = () => this.clear()
    this.onBeforeFetch = event => this.useForRequest(event)
    this.onLinkPrefetch = event => this.prefetchPage(event)
    this.onLinkLeave = event => {
      if (event.target === this.entry?.link && !this.entry.claimed) this.clear()
    }
    this.onBeforeVisit = event => this.claim(event.detail?.url, "page")
    document.addEventListener("resource-preview:prefetch", this.onSchedule)
    document.addEventListener("resource-preview:prefetch-cancel", this.onCancel)
    document.addEventListener("turbo:before-fetch-request", this.onBeforeFetch, true)
    document.addEventListener("turbo:before-prefetch", this.onLinkPrefetch)
    document.addEventListener("turbo:before-visit", this.onBeforeVisit)
    document.addEventListener("mouseleave", this.onLinkLeave, true)
    document.addEventListener("turbo:submit-start", this.onInvalidate)
    document.addEventListener("turbo:before-stream-render", this.onInvalidate)
    document.addEventListener("resource-preview:invalidate", this.onInvalidate)
    document.addEventListener("turbo:before-cache", this.onBeforeCache)
    window.addEventListener("task-actioned", this.onInvalidate)
    window.addEventListener("tasks-bulk-actioned", this.onInvalidate)
    window.addEventListener("process-table:refresh-requested", this.onInvalidate)
  }

  disconnect() {
    this.disconnected = true
    this.clear()
    for (const entry of this.active) {
      if (entry.kind === "preview") {
        entry.cancelled = true
        entry.requestAbort.abort()
      }
    }
    document.removeEventListener("resource-preview:prefetch", this.onSchedule)
    document.removeEventListener("resource-preview:prefetch-cancel", this.onCancel)
    document.removeEventListener("turbo:before-fetch-request", this.onBeforeFetch, true)
    document.removeEventListener("turbo:before-prefetch", this.onLinkPrefetch)
    document.removeEventListener("turbo:before-visit", this.onBeforeVisit)
    document.removeEventListener("mouseleave", this.onLinkLeave, true)
    document.removeEventListener("turbo:before-cache", this.onBeforeCache)
    // A cached Turbo snapshot can disconnect this controller before its page
    // request finishes. Keep invalidation listeners until that request settles.
    if (!this.active.size) this.removeInvalidationListeners()
  }

  removeInvalidationListeners() {
    document.removeEventListener("turbo:submit-start", this.onInvalidate)
    document.removeEventListener("turbo:before-stream-render", this.onInvalidate)
    document.removeEventListener("resource-preview:invalidate", this.onInvalidate)
    window.removeEventListener("task-actioned", this.onInvalidate)
    window.removeEventListener("tasks-bulk-actioned", this.onInvalidate)
    window.removeEventListener("process-table:refresh-requested", this.onInvalidate)
  }

  previewUrl(value) {
    try {
      const url = new URL(value, window.location.href)
      if (url.origin !== window.location.origin || url.search || url.hash) return null
      if (!/^\/(tasks|processes)\/[^/]+\/preview$/.test(url.pathname)) return null
      return url.href
    } catch (_) {
      return null
    }
  }

  pageUrl(value) {
    try {
      const url = new URL(value, window.location.href)
      if (url.origin !== window.location.origin || !/^\/(tasks|processes)\/[A-Z]+-\d+$/.test(url.pathname)) return null
      const params = [...url.searchParams]
      if (params.length > 1) return null
      if (params.length === 1) {
        const [name, value] = params[0]
        const backToList = name === "back" && value === "1"
        const fromTask = url.pathname.startsWith("/processes/") && name === "from_task" && /^[A-Z]+-\d+$/.test(value)
        if (!backToList && !fromTask) return null
      }
      url.hash = ""
      return url.href
    } catch (_) {
      return null
    }
  }

  speculationAllowed(now) {
    const connection = navigator.connection
    if (connection?.saveData || ["slow-2g", "2g"].includes(connection?.effectiveType)) return false
    // Turbo's own off switch for link prefetching turns this one off too.
    if (document.querySelector('meta[name="turbo-prefetch"]')?.content === "false") return false
    return budget.allows(now)
  }

  schedule(value, kind = "preview", link = null) {
    const url = kind === "page" ? this.pageUrl(value) : this.previewUrl(value)
    const now = performance.now()
    if (!url || !this.speculationAllowed(now)) return
    // A claim whose request never arrived (another handler cancelled the visit)
    // expires like any entry, so it cannot block later hovers.
    const current = this.entry
    if (current && now - current.createdAt < MAX_AGE_MS && (current.claimed || (current.kind === kind && current.url === url))) return

    this.clear()
    const entry = { url, kind, link, createdAt: now, claimed: false }
    this.entry = entry
    const delay = Math.max(INTENT_DELAY_MS, budget.spacing(now))
    entry.timer = setTimeout(() => {
      if (this.entry !== entry) return
      entry.timer = null
      const startedAt = performance.now()
      // A 429 can arrive while this start waits its turn. Drop it, never queue it.
      if (!budget.allows(startedAt)) {
        this.entry = null
        return
      }
      entry.createdAt = startedAt
      budget.record(startedAt)
      entry.abort = new AbortController()
      const headers = { Accept: "text/html, application/xhtml+xml", "X-Sec-Purpose": "prefetch" }
      if (entry.kind === "preview") headers["Turbo-Frame"] = this.frame.id
      entry.response = Turbo.fetch(entry.url, {
        method: "GET", credentials: "same-origin", redirect: "follow", cache: "no-store", priority: "low",
        headers,
        signal: entry.abort.signal,
      }).then(async response => {
        if (response.status === 429) budget.pause(performance.now())
        const contentType = response.headers.get("content-type") || ""
        if (!response.ok || response.redirected || !contentType.includes("text/html")) return null
        // Fetch resolves at the headers; Turbo still needs the whole body.
        // Keep the request cancellable until the complete response is ready.
        await response.clone().arrayBuffer()
        return response
      }).catch(() => null)
    }, delay)
  }

  prefetchPage(event) {
    const link = event.target
    // Turbo's own cache cannot be invalidated after a live update. Keep these
    // requests in our cache so a mutation always makes them unusable.
    let location
    try { location = new URL(link?.href, window.location.href) } catch (_) { return }
    if (location.origin !== window.location.origin || !/^\/(tasks|processes)\/[A-Z]+-\d+$/.test(location.pathname)) return
    event.preventDefault()
    const url = this.pageUrl(location.href)
    if (!url) return
    const frame = link.closest?.("turbo-frame")
    const target = link.getAttribute?.("data-turbo-frame") || frame?.getAttribute("target") || frame?.id
    if (target && target !== "_top") return
    this.schedule(url, "page", link)
  }

  cancel(value) {
    if (!this.entry || this.entry.claimed) return
    if (!value || this.previewUrl(value) === this.entry.url) this.clear()
  }

  // Claim before showing the drawer: its animation can move the pointer out
  // of the row before Turbo starts the frame request.
  claim(value, kind = "preview") {
    const entry = this.entry
    const url = kind === "page" ? this.pageUrl(value) : this.previewUrl(value)
    if (!entry || entry.kind !== kind || entry.url !== url || !entry.response || performance.now() - entry.createdAt >= MAX_AGE_MS) {
      this.clear()
      return
    }
    entry.claimed = true
  }

  useForRequest(event) {
    const entry = this.entry
    if (!entry?.claimed || event.detail.fetchOptions.method !== "GET") return
    if (entry.kind === "preview" ? event.target !== this.frame : event.target !== document.documentElement) return
    const url = entry.kind === "page" ? this.pageUrl(event.detail.url.href) : this.previewUrl(event.detail.url.href)
    if (entry.url !== url || performance.now() - entry.createdAt >= MAX_AGE_MS) {
      this.clear()
      return
    }
    this.entry = null
    if (event.detail.fetchRequest) {
      entry.abort.abort()
      return
    }
    // Turbo uses the same handoff for its own link cache. Keep claimed entries
    // tracked until they settle so live updates can invalidate them as well.
    entry.revision = 0
    entry.requestAbort = entry.abort
    this.active.add(entry)
    const options = event.detail.fetchOptions
    const signal = options.signal
    const abortError = () => Object.assign(new Error("Request cancelled"), { name: "AbortError" })
    const fetchFresh = async () => {
      let attempts = 0
      while (true) {
        // Coalesce a burst of live updates after a retry has already started.
        if (attempts > 0) await new Promise(resolve => setTimeout(resolve, INTENT_DELAY_MS))
        if (entry.cancelled || signal?.aborted) throw abortError()
        entry.finalAttempt = ++attempts >= MAX_FRESH_ATTEMPTS
        const revision = entry.revision
        const abort = new AbortController()
        entry.requestAbort = abort
        try {
          const result = await Turbo.fetch(event.detail.url.href, { ...options, signal: abort.signal })
          await result.clone().arrayBuffer()
          if (entry.cancelled || signal?.aborted) throw abortError()
          // Under a continuous stream of updates, finish a normal navigation
          // rather than retrying forever. It has the ordinary request's race.
          if (entry.finalAttempt || entry.revision === revision) return result
        } catch (error) {
          if (entry.cancelled || signal?.aborted) throw abortError()
          if (entry.finalAttempt || entry.revision === revision) throw error
        }
      }
    }
    const response = entry.response.then(result => {
      if (entry.cancelled || signal?.aborted) throw abortError()
      return entry.revision === 0 && result ? result : fetchFresh()
    })
    // A later frame navigation cancels Turbo's request. Propagate that signal
    // to the shared prefetch too, so its late response cannot replace a newer
    // row's preview.
    const guardedResponse = signal ? new Promise((resolve, reject) => {
      const onAbort = () => { entry.cancelled = true; entry.requestAbort.abort(); reject(abortError()) }
      if (signal.aborted) onAbort()
      else signal.addEventListener("abort", onAbort, { once: true })
      response.then(
        value => { signal.removeEventListener("abort", onAbort); resolve(value) },
        error => { signal.removeEventListener("abort", onAbort); reject(error) },
      )
    }) : response
    event.detail.fetchRequest = { response: guardedResponse.finally(() => {
      this.active.delete(entry)
      if (this.disconnected && !this.active.size) this.removeInvalidationListeners()
    }) }
  }

  invalidate() {
    this.clear()
    for (const entry of this.active) {
      entry.revision++
      if (!entry.finalAttempt) entry.requestAbort.abort()
    }
  }

  clear() {
    const entry = this.entry
    this.entry = null
    if (!entry) return
    clearTimeout(entry.timer)
    entry.abort?.abort()
  }
}

// Title links prepare a full page; non-interactive rows prepare the drawer.
export function bindRowPreviewPrefetch(table, { type, isInteractive, isGroup, isSelectionPreview }) {
  const urlFor = row => {
    const data = row.getData()
    return !data.reference || isGroup(data) ? null : `/${type}/${data.reference}/preview`
  }
  const dispatch = (name, url) => document.dispatchEvent(new CustomEvent(name, { detail: { url } }))
  // Any reload of the rows (a bulk change, a refresh) can change what a show or
  // preview page says, so a held response must not outlive it.
  table.on("dataLoaded", () => dispatch("resource-preview:invalidate", null))
  table.on("rowMouseOver", (event, row) => {
    const url = urlFor(row)
    if (!url) return
    if (isInteractive(event.target) || event.target.closest(".tabulator-row-select") || isSelectionPreview(row)) {
      dispatch("resource-preview:prefetch-cancel", url)
    } else {
      dispatch("resource-preview:prefetch", url)
    }
  })
  table.on("rowMouseOut", (event, row) => {
    if (event.relatedTarget && row.getElement().contains(event.relatedTarget)) return
    const url = urlFor(row)
    if (url) dispatch("resource-preview:prefetch-cancel", url)
  })
}
